// Consulta GLOBAL de reclamos de aseguradora (todos los pacientes), con filtros.
// RPC med_reclamos_listar (script 399). Tabla ordenable + N° + fila de totales;
// descarga del formulario oficial y del superbill por fila. Menú Centro Medico →
// Clínica → "Consulta de reclamos".
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Eye, FileSearch, Loader2, X } from 'lucide-react';
import { abrirAdjunto } from './ExamenesFicha';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useSort, SortableTh } from '@/features/financiero/tablaSort';
import { Campo, fieldCls, medRpc } from './shared';

interface Row {
  id: string; numero: number; fecha: string; estado: string | null;
  aseguradora: string | null; num_asegurado: string | null;
  paciente: string; identificacion: string | null; mision: string | null;
  costo_total: number; moneda: string; n_archivos: number;
}
interface Formato { id: string; codigo: string; nombre: string }

const ESTADOS: Record<string, { t: string; cls: string }> = {
  encolado: { t: 'Generando…', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  procesando: { t: 'Generando…', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  generado: { t: 'Listo', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  error: { t: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const fmtF = (v?: string | null) => { if (!v) return '—'; const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v); };
const money = (n: number, m: string) => `${m} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ConsultaReclamos() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [aseg, setAseg] = useState('');
  const [estado, setEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState({ desde: '', hasta: '', aseg: '', estado: '', busqueda: '' });

  const { data: formatos = [] } = useQuery<Formato[]>({
    queryKey: ['med-aseg-formatos'], queryFn: () => medRpc<Formato[]>('med_aseguradora_formatos', {}),
  });
  const { data: rows = [], isFetching } = useQuery<Row[]>({
    queryKey: ['med-reclamos-listar', q],
    queryFn: () => medRpc<Row[]>('med_reclamos_listar', {
      _desde: q.desde || null, _hasta: q.hasta || null, _aseguradora: q.aseg || null,
      _estado: q.estado || null, _busqueda: q.busqueda || null,
    }),
  });

  const buscar = () => setQ({ desde, hasta, aseg, estado, busqueda });
  const limpiar = () => { setDesde(''); setHasta(''); setAseg(''); setEstado(''); setBusqueda(''); setQ({ desde: '', hasta: '', aseg: '', estado: '', busqueda: '' }); };

  const { sorted, sort, toggle } = useSort<Row>(rows);
  const total = useMemo(() => rows.reduce((s, r) => s + Number(r.costo_total || 0), 0), [rows]);
  const moneda = rows[0]?.moneda || 'USD';
  const porAseg = useMemo(() => agrupar(rows, (r) => r.aseguradora || '—'), [rows]);
  const porMision = useMemo(() => agrupar(rows, (r) => r.mision || 'Sin misión'), [rows]);

  const exportCsv = () => {
    const head = ['N°', 'Fecha', 'Paciente', 'Identificación', 'Aseguradora', 'N° asegurado', 'Misión', 'Costo', 'Moneda', 'Estado'];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = sorted.map((r, i) => [i + 1, fmtF(r.fecha), r.paciente, r.identificacion, r.aseguradora, r.num_asegurado, r.mision, Number(r.costo_total || 0).toFixed(2), r.moneda, ESTADOS[r.estado ?? '']?.t ?? r.estado].map(esc).join(','));
    const csv = [head.map(esc).join(','), ...lines].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'reclamos-aseguradora.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <FileSearch className="w-5 h-5" /> Consulta de reclamos de aseguradora
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
            <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            <Campo label="Aseguradora">
              <select className={fieldCls} value={aseg} onChange={(e) => setAseg(e.target.value)}>
                <option value="">Todas</option>{formatos.map((f) => <option key={f.id} value={f.codigo}>{f.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Estado">
              <select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="">Todos</option><option value="generado">Listo</option>
                <option value="procesando">Generando</option><option value="error">Error</option>
              </select>
            </Campo>
            <Campo label="Buscar (paciente / misión / N° asegurado)" className="sm:col-span-2 lg:col-span-1">
              <input className={fieldCls} value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && buscar()} placeholder="texto…" />
            </Campo>
            <div className="flex gap-2">
              <Button size="sm" onClick={buscar}>Buscar</Button>
              <Button size="sm" variant="outline" onClick={limpiar}><X className="w-4 h-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <ResumenTabla titulo="Totales por aseguradora" labelCol="Aseguradora" filas={porAseg} moneda={moneda} />
          <ResumenTabla titulo="Totales por misión" labelCol="Misión" filas={porMision} moneda={moneda} />
        </div>
      )}

      <Card>
        <CardContent className="pt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{rows.length} reclamo(s)</span>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={rows.length === 0}><Download className="w-4 h-4 mr-1" /> CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 text-left">N°</th>
                  <SortableTh col="fecha" label="Fecha" sort={sort} onSort={toggle} />
                  <SortableTh col="paciente" label="Paciente" sort={sort} onSort={toggle} />
                  <SortableTh col="identificacion" label="Identificación" sort={sort} onSort={toggle} />
                  <SortableTh col="aseguradora" label="Aseguradora" sort={sort} onSort={toggle} />
                  <SortableTh col="num_asegurado" label="N° asegurado" sort={sort} onSort={toggle} />
                  <SortableTh col="mision" label="Misión" sort={sort} onSort={toggle} />
                  <SortableTh col="costo_total" label="Costo" align="right" sort={sort} onSort={toggle} />
                  <SortableTh col="estado" label="Estado" align="center" sort={sort} onSort={toggle} />
                  <th className="py-2 pr-3 text-center">PDF</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin reclamos para los filtros.</td></tr>}
                {sorted.map((r, i) => {
                  const e = ESTADOS[r.estado ?? ''] ?? { t: r.estado ?? '—', cls: 'bg-muted text-muted-foreground' };
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fmtF(r.fecha)} <span className="text-muted-foreground">#{r.numero}</span></td>
                      <td className="py-1.5 pr-3">{r.paciente}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{r.identificacion || '—'}</td>
                      <td className="py-1.5 pr-3">{r.aseguradora || '—'}</td>
                      <td className="py-1.5 pr-3">{r.num_asegurado || '—'}</td>
                      <td className="py-1.5 pr-3">{r.mision || '—'}</td>
                      <td className="py-1.5 pr-3 text-right whitespace-nowrap">{money(r.costo_total, r.moneda)}</td>
                      <td className="py-1.5 pr-3 text-center"><span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.t}</span></td>
                      <td className="py-1.5 pr-3 text-center"><Descargas reclamoId={r.id} n={r.n_archivos} /></td>
                    </tr>
                  );
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="border-t font-medium">
                  <tr>
                    <td className="py-2 pr-3" colSpan={7}>Total ({rows.length})</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{money(total, moneda)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Agrupa las filas por una clave → [{ clave, reclamos, costo }] ordenado por costo desc
interface Grupo { clave: string; reclamos: number; costo: number }
function agrupar(rows: Row[], key: (r: Row) => string): Grupo[] {
  const m = new Map<string, Grupo>();
  for (const r of rows) {
    const k = key(r);
    const g = m.get(k) ?? { clave: k, reclamos: 0, costo: 0 };
    g.reclamos += 1; g.costo += Number(r.costo_total || 0);
    m.set(k, g);
  }
  return [...m.values()].sort((a, b) => b.costo - a.costo);
}

// Mini-tabla de subtotales (ordenable + fila de total)
function ResumenTabla({ titulo, labelCol, filas, moneda }: { titulo: string; labelCol: string; filas: Grupo[]; moneda: string }) {
  const { sorted, sort, toggle } = useSort<Grupo>(filas);
  const totCosto = filas.reduce((s, f) => s + f.costo, 0);
  const totRec = filas.reduce((s, f) => s + f.reclamos, 0);
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="mb-2 text-sm font-medium">{titulo}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3 text-left w-8">N°</th>
                <SortableTh col="clave" label={labelCol} sort={sort} onSort={toggle} />
                <SortableTh col="reclamos" label="Reclamos" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="costo" label="Costo" align="right" sort={sort} onSort={toggle} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((g, i) => (
                <tr key={g.clave} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3">{g.clave}</td>
                  <td className="py-1.5 pr-3 text-right">{g.reclamos}</td>
                  <td className="py-1.5 pr-3 text-right whitespace-nowrap">{money(g.costo, moneda)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr>
                <td className="py-2 pr-3" colSpan={2}>Total</td>
                <td className="py-2 pr-3 text-right">{totRec}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">{money(totCosto, moneda)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Descarga perezosa de los PDF del reclamo (formulario + superbill)
function Descargas({ reclamoId, n }: { reclamoId: string; n: number }) {
  const [open, setOpen] = useState(false);
  const { data: archivos = [], isFetching } = useQuery<{ id: string; nombre: string; archivo: string }[]>({
    queryKey: ['med-reclamo-archivos', reclamoId],
    queryFn: () => medRpc<{ id: string; nombre: string; archivo: string }[]>('med_examen_archivos', { _examen_id: reclamoId }),
    enabled: open,
  });
  if (n === 0) return <span className="text-xs text-muted-foreground">—</span>;
  if (!open) return <button className="text-primary hover:underline text-xs inline-flex items-center gap-1" onClick={() => setOpen(true)}><Download className="w-3.5 h-3.5" /> {n}</button>;
  if (isFetching) return <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />;
  return (
    <div className="flex flex-col items-start gap-0.5">
      {archivos.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-2">
          <a href={a.archivo} download={a.nombre} title={a.nombre} className="text-primary hover:underline text-xs inline-flex items-center gap-1">
            <Download className="w-3 h-3" /> {a.nombre.startsWith('Superbill') ? 'Superbill' : 'Formulario'}
          </a>
          <button type="button" className="text-primary hover:underline text-xs inline-flex items-center gap-1" title="Ver en una pestaña nueva" onClick={() => abrirAdjunto(a.archivo)}>
            <Eye className="w-3 h-3" /> Ver
          </button>
        </span>
      ))}
    </div>
  );
}
